import {
  AvatarCurrency,
  ButtonLink,
  ContentIsland,
  ErrorListItem,
  Grid,
  IconButton,
  ListContentIsland,
  Menu,
  MenuItem,
  Money,
  MoreIcon,
  SectionedGridContainer,
  Stack,
  Typography,
  useAnchor,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useAccountQuery } from '../../../api/accounts';
import { useCallback } from 'react';
import { TerminateAccountDialog } from './TerminateAccountDialog';
import { useBoolean } from 'usehooks-ts';
import { useHasRole } from '../../../api/access';

const discontinuedCurrencies = __BRIGHTY__ ? ['USDT'] : [];

export function AccountSummary({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const isAdmin = useHasRole('ADMIN');
  const isPayer = useHasRole('PAYER');
  const isViewer = useHasRole('VIEWER');
  const accountQuery = useAccountQuery({ accountId: accountId });
  const anchor = useAnchor<HTMLButtonElement>();
  const terminateFlag = useBoolean();

  const isBase = accountQuery.data?.purpose === 'BASE';
  const isTerminatable = isAdmin && !isBase;

  const currency = accountQuery.data?.balance.currency;

  const handleClickTerminateAccount = useCallback(() => {
    anchor.onClose();
    terminateFlag.setTrue();
  }, [anchor, terminateFlag]);

  return (
    <Stack spacing={2} width="100%">
      <ContentIsland
        title={t(`Total balance`)}
        actions={
          isTerminatable ? (
            <IconButton onClick={anchor.onOpen}>
              <MoreIcon />
            </IconButton>
          ) : null
        }
      >
        {isTerminatable ? (
          <Menu
            anchorEl={anchor.element}
            open={anchor.open}
            onClose={anchor.onClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleClickTerminateAccount}>
              <Typography color="error">{t('Close account')}</Typography>
            </MenuItem>
          </Menu>
        ) : null}
        <SectionedGridContainer justifyContent="space-between">
          <Grid
            gap={2}
            alignItems="center"
            display="flex"
            size={{
              md: 6,
            }}
          >
            <Money
              variant="h2"
              loading={accountQuery.isPending}
              value={accountQuery.data?.balance}
            />
            {accountQuery.data && (
              <AvatarCurrency currency={accountQuery.data.balance.currency} size="small" />
            )}
          </Grid>
          <Grid
            display="flex"
            gap={1}
            justifyContent="flex-end"
            size={{
              md: 6,
            }}
          >
            {isPayer ? (
              <ButtonLink to="/account/exchange" search={{ accountId }}>
                {t('Exchange')}
              </ButtonLink>
            ) : null}
            {isPayer ? (
              <ButtonLink to="/account/transfer" search={{ accountId }}>
                {t('Send')}
              </ButtonLink>
            ) : null}
            {isViewer ? (
              <ButtonLink to="/account/statements" search={{ accountId }}>
                {t('Statements')}
              </ButtonLink>
            ) : null}
          </Grid>
        </SectionedGridContainer>
        {accountQuery.data && (
          <TerminateAccountDialog
            open={terminateFlag.value}
            onClose={terminateFlag.setFalse}
            account={accountQuery.data}
          />
        )}
      </ContentIsland>
      {currency && discontinuedCurrencies.includes(currency) ? (
        <ListContentIsland>
          <ErrorListItem
            error={t(
              "{{companyName}} discontinued support of {{currency}}.\nIf you have existing deposit address it will work but you won't be able to exchange it or transfer it.",
              { currency },
            )}
          />
        </ListContentIsland>
      ) : null}
    </Stack>
  );
}
